import React, { useState, useEffect, useCallback } from 'react';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const statusBadge = v => {
  const map = {
    valid:         'bg-green-100 text-green-700',
    expiring_soon: 'bg-amber-100 text-amber-700',
    expired:       'bg-red-100 text-red-700',
    unknown:       'bg-slate-100 text-slate-500',
  };
  const labels = { valid: 'Valid', expiring_soon: 'Expiring Soon', expired: 'Expired', unknown: 'Unknown' };
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', map[v] || 'bg-slate-100 text-slate-600')}>{labels[v] || v}</span>;
};

const COLS = [
  { key: 'staff_name',          label: 'Staff Name' },
  { key: 'role',                label: 'Role',       render: v => v ? v.replace(/_/g, ' ') : '–' },
  { key: 'pay_group',           label: 'Pay Group' },
  { key: 'car_registration',    label: 'Registration' },
  { key: 'car_make',            label: 'Make' },
  { key: 'car_model',           label: 'Model' },
  { key: 'car_color',           label: 'Color' },
  { key: 'car_expiry',          label: 'Rego Expiry' },
  { key: 'days_until_expiry',   label: 'Days Until Expiry', render: v => v != null ? `${v} days` : '–' },
  { key: 'rego_status',         label: 'Status',     render: statusBadge },
];

export default function CarRegoReport() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('all');
  const pagination = usePagination(data);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ status });
    if (search) p.set('search', search);
    api.get(`/reports/car-rego?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const chips = [
    ...(status !== 'all' ? [{ label: 'Status', value: status, onRemove: () => setStatus('all') }] : []),
    ...(search ? [{ label: 'Search', value: search, onRemove: () => setSearch('') }] : []),
  ];

  const counts = {
    valid:         data.filter(r => r.rego_status === 'valid').length,
    expiring_soon: data.filter(r => r.rego_status === 'expiring_soon').length,
    expired:       data.filter(r => r.rego_status === 'expired').length,
    unknown:       data.filter(r => r.rego_status === 'unknown').length,
  };

  return (
    <ReportLayout title="Car Rego Report" data={data} filename="car_rego_report" columns={COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Search Staff</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Staff name..." className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Rego Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="input-field !w-auto text-sm">
              <option value="all">All</option>
              <option value="valid">Valid</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expired">Expired</option>
              <option value="unknown">Unknown / Not Recorded</option>
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Valid',          value: counts.valid,         color: 'text-green-600' },
          { label: 'Expiring Soon',  value: counts.expiring_soon, color: 'text-amber-600' },
          { label: 'Expired',        value: counts.expired,       color: 'text-red-600' },
          { label: 'Not Recorded',   value: counts.unknown,       color: 'text-slate-500' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>
      <div className="mb-3 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-xl p-3">
        To record vehicle details for a staff member, go to their Staff Profile and update the Car / Vehicle section.
      </div>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
